/* eslint-disable react/no-array-index-key */
import * as React from 'react';
import { InView } from 'react-intersection-observer';

import { uniq } from 'lodash';
import { useStore } from 'zustand';

import { ButtonModel, IconRegularChevronLeft } from '@notino/react-styleguide';

import { ReviewPhotoModal } from '@lib/reviews/ReviewPhotoModal';

import { PhotosStoreStore } from '../store/createPhotosStore';

import {
  BackButton,
  Grid,
  GridImageButton,
  GridImageSkeleton,
  ReviewPhotoModalHeader,
} from './styled';
import {
  findNextReviewFirstPhoto,
  findPrevReviewLastPhoto,
  useRestoreGridScroll,
} from './utils';

type Props = {
  store: PhotosStoreStore;
};

export const PhotosGrid = ({ store }: Props) => {
  const {
    photos,
    hasMore,
    selectedPhoto,
    actions: { fetchMorePhotos, setSelectedPhoto },
  } = useStore(store);

  const { imageRefs } = useRestoreGridScroll(selectedPhoto);

  if (selectedPhoto) {
    const photoIndex = photos.findIndex(
      (p) => p.fullSizeUrl === selectedPhoto.fullSizeUrl
    );
    const reviewIds = uniq(photos.map((p) => p.reviewId));
    const reviewIndex = reviewIds.indexOf(selectedPhoto.reviewId);
    const lastReviewIndex = reviewIds.length - 1;

    return (
      <div key={selectedPhoto.reviewId}>
        <ReviewPhotoModalHeader>
          <BackButton
            buttonStyle={ButtonModel.Styles.secondary}
            onClick={() => setSelectedPhoto(undefined)}
          >
            <IconRegularChevronLeft width="24px" height="24px" />
          </BackButton>
        </ReviewPhotoModalHeader>

        <ReviewPhotoModal
          reviewId={selectedPhoto.reviewId}
          initialImage={selectedPhoto}
          showPrevReviewArrow={reviewIndex > 0}
          showNextReviewArrow={reviewIndex < lastReviewIndex}
          onPrevReviewClick={() =>
            setSelectedPhoto(
              findPrevReviewLastPhoto(photos, photoIndex) ?? undefined
            )
          }
          onNextReviewClick={() => {
            const nextPhoto = findNextReviewFirstPhoto(photos, photoIndex);
            setSelectedPhoto(nextPhoto ?? undefined);

            const isNextReviewLast = nextPhoto
              ? reviewIds.indexOf(nextPhoto.reviewId) === lastReviewIndex
              : false;

            if (isNextReviewLast && hasMore) {
              void fetchMorePhotos();
            }
          }}
        />
      </div>
    );
  }

  return (
    <Grid>
      {photos.map((image, index) => (
        <GridImageButton
          type="button"
          key={image.fullSizeUrl}
          ref={(el) => {
            if (el) {
              imageRefs.current[image.fullSizeUrl] = el;
            }
          }}
          onClick={() => setSelectedPhoto(image)}
        >
          <img
            src={image.thumbnailUrl ?? undefined}
            alt={`Review ${index + 1}`}
          />
        </GridImageButton>
      ))}

      {hasMore && (
        // fragment key ensures <InView> triggers again when more photos are loaded
        <React.Fragment key={photos.length}>
          {Array.from({ length: 8 }).map((_, index) => (
            <InView
              key={index}
              triggerOnce={true}
              onChange={(inView) => inView && index === 0 && fetchMorePhotos()}
            >
              <GridImageSkeleton />
            </InView>
          ))}
        </React.Fragment>
      )}
    </Grid>
  );
};
